import { Button } from "primereact/button";
import { useEffect, useRef, useState, useCallback } from "react";
import ManFigure, { ManFigureRef } from "./Figures/manFigure";
import WomanFigure, { WomanFigureRef } from "./Figures/womanFigure";
import AnatomyModel3D, { AnatomyModel3DRef } from "../../../Home/AnatomyModel3D";

const Step1 = ({
    selectedPatient,
    figureContainerRef,
    t,
    step,
    setStep,
    setSelectedExercises,
    setPanels,
    setManuelSelectedExercises,
    setActivePanelIndex,
    selectedFigures,
    setSelectedFigures,
    setSelectedFiguresForFilter,
    selectedIllnessList,
    applyButtonVisible,
    setApplyButtonVisible,
    setSelectedPanels,
    setEditedExercises,
    setClickedPanels,
     }: any) => {


    const womanImages = ["/woman_muscular_system.png", "/woman_muscular_system_back.png", "/skeletal_system_woman.png", "/skeletal_system_posterior_view_woman.png"];
    const manImages = ["/man_muscular_system.png", "/man_muscular_system_back.png", "/skeletal_system_man.png", "/skeletal_system_posterior_view_man.png"];


    const [bodyImages, setBodyImages] = useState<any>([]);


    const previousSelectedFiguresRef = useRef<any[]>([]);

    const womanFigureRef = useRef<WomanFigureRef>(null);
    const manFigureRef = useRef<ManFigureRef>(null);
    const anatomyModelRef = useRef<AnatomyModel3DRef>(null);

    const areBodyPartsSame = (figures1: any[], figures2: any[]): boolean => {
        if (figures1.length !== figures2.length) return false;
        const ids1 = new Set(figures1.map((f: any) => f.id).sort());
        const ids2 = new Set(figures2.map((f: any) => f.id).sort());
        if (ids1.size !== ids2.size) return false;
        for (const id of ids1) {
            if (!ids2.has(id)) return false;
        }
        return true;
    };

    const handleAreaSelected = useCallback(() => {
        if (step !== 1) {
            setStep(1);
        }
    }, [step, setStep]);

    const handleApply = () => {
        if (selectedFigures.length > 0) {
            const mappedStringsSet = new Set<string>();

            selectedFigures.forEach((figure: any) => {
                if (figure.englishName) {
                    mappedStringsSet.add(figure.englishName);
                }
            });

            previousSelectedFiguresRef.current = [...selectedFigures];
            setSelectedFiguresForFilter(Array.from(mappedStringsSet));
            setStep(2);
        }
    };

    useEffect(() => {
        if (selectedPatient?.gender === "Female") {
            setBodyImages(womanImages);
        } else {
            setBodyImages(manImages);
        }

    }, []);

    useEffect(() => {
        if (!figureContainerRef.current) return;

        const resizeObserver = new ResizeObserver(() => {
            const resizeEvent = new Event('resize');
            window.dispatchEvent(resizeEvent);
        });

        resizeObserver.observe(figureContainerRef.current);

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    useEffect(() => {
        if (step === 1) {
            if (selectedIllnessList.length !== 0 && previousSelectedFiguresRef.current.length > 0) {
                const bodyPartsAreSame = areBodyPartsSame(selectedFigures, previousSelectedFiguresRef.current);
                if (bodyPartsAreSame) {
                    setApplyButtonVisible(false);
                } else {
                    setApplyButtonVisible(true);
                }
            } else {
                setApplyButtonVisible(true);
            }
        }
    }, [selectedFigures, selectedIllnessList, step]);



    return (
        <div
            className="flex flex-column"
            style={{
                flex: step === 1 ? "0 0 41.666667%" : step === 4 ? "0 1 12%" : "1 1 0%",
                backgroundColor: step === 1 ? "#D4DDFF" : "#E8EDFF",
                borderRadius: "16px",
                height: "100%",
                overflow: "hidden",
                padding: 0,
                position: "relative",
                cursor: step !== 1 ? "pointer" : "default",
                transition: "flex 0.4s ease-in-out, background-color 0.4s ease-in-out",
                minWidth: 0
            }}
            onClick={step !== 1 ? () => {
                if(selectedIllnessList?.length === 0) {
                    setSelectedPanels([]);
                    setPanels([]);
                    setManuelSelectedExercises([]);
                    setEditedExercises([]);
                    setActivePanelIndex(-1);
                    setClickedPanels([]);
                }
                setStep(1);
                setSelectedExercises([]);
            } : undefined}
        >
            {step === 1 && <p
                className="m-0 font-bold text-xl"
                style={{
                    color: "#2D3967",
                    position: "absolute",
                    top: "1.5rem",
                    left: "1.5rem",
                    zIndex: 1000,
                    pointerEvents: "none"
                }}
            >
                {t("selectTheView")}
            </p>}

            <div ref={figureContainerRef} style={{
                height: "100%",
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: 0,
                flexShrink: 1
            }}>
                <AnatomyModel3D 
                    key="anatomy-3d-model" 
                    ref={anatomyModelRef}
                    height="100%" 
                    selectedFigures={selectedFigures}
                    setSelectedFigures={setSelectedFigures}
                    onAreaSelected={handleAreaSelected}
                    step={step}
                    t={t}
                />
            </div>

            {step === 1 &&
                <div
                    className="flex justify-content-between align-items-center"
                    style={{
                        position: "absolute",
                        bottom: "1.5rem",
                        left: "1.5rem",
                        right: "1.5rem",
                        zIndex: 1000
                    }}
                >
                    <div className="flex align-items-center justify-content-center  gap-2" style={{ cursor: "pointer" }} onClick={() => {
                        womanFigureRef.current?.handleReset();
                        manFigureRef.current?.handleReset();
                        anatomyModelRef.current?.handleReset();
                        setSelectedFigures([]);
                        previousSelectedFiguresRef.current = [];
                        setApplyButtonVisible(true);
                    }}>
                        <i className="pi pi-trash" style={{ color: "#727A99" }}></i>
                        <p className="m-0 text-sm underline" style={{ color: "#727A99" }}>{t("clearChoice")}</p>
                    </div>

                    {applyButtonVisible &&
                        <Button label={t("apply")} className="p-3"
                            style={{ backgroundColor: "#6383F4", color: "#FFFFFF", borderRadius: "16px", border: "none", width: "6rem", height: "2.5rem" }}
                            onClick={handleApply}
                            disabled={selectedFigures.length === 0}
                        />}
                </div>
            }
        </div>
    )
}

export default Step1;